/**
 * Fetch work-queue style items from each RCM service backend.
 * Services expose `/api/v1/work-queue/` backed by Task Manager (SSOT).
 * Domain narrative: `rcmWorkDomain.js`.
 */
import axios from 'axios';

/** Human-readable queue IDs per service (stable per domain record). Shown in service + AgenticRCM shell. */
const DISPLAY_ID_PREFIX = {
  denial_management: 'Denial',
  claims_processing: 'Claims-Processing',
  insurance_verification: 'Insurance-Verification',
  prior_authorization: 'Prior-Auth',
  ar_posting: 'AR-Posting',
  charge_capture: 'Charge-Capture',
  remittance_advice: 'Remittance',
  patient_billing: 'Patient-Billing',
  contract_management: 'Contract',
  rcm_compliance: 'Compliance',
  /** AgenticPractice unified queue */
  request_management: 'Request-Management',
  scheduling_waitlist: 'Scheduling-Waitlist',
};

/**
 * Deterministic 3-digit sequence (001–999) from a domain id (UUID, claim id, etc.).
 * Same record always gets the same display suffix across loads and shells.
 */
export function stableSequenceFromDomainId(domainId) {
  const s = String(domainId ?? '');
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  const n = (Math.abs(h) % 999) + 1;
  return String(n).padStart(3, '0');
}

/**
 * @param {string} serviceKey — key in RCM_QUEUE_SERVICES
 * @param {string|number} domainId — stable id from the domain (denial/claim row id)
 * @returns {string} e.g. Denial-042, Claims-Processing-015
 */
export function buildDisplayQueueId(serviceKey, domainId) {
  const prefix = DISPLAY_ID_PREFIX[serviceKey] || 'Queue';
  const seq = stableSequenceFromDomainId(domainId);
  return `${prefix}-${seq}`;
}

export const RCM_QUEUE_SERVICES = {
  insurance_verification: {
    name: 'Insurance Verification',
    port: 8150,
    listMode: 'work_queue',
    endpoint: '/api/v1/work-queue/',
  },
  prior_authorization: {
    name: 'Prior Authorization',
    port: 8151,
    listMode: 'work_queue',
    endpoint: '/api/v1/work-queue/',
  },
  denial_management: {
    name: 'Denial Management',
    port: 8154,
    listMode: 'work_queue',
    endpoint: '/api/v1/work-queue/',
  },
  ar_posting: {
    name: 'AR Posting',
    port: 8155,
    listMode: 'work_queue',
    endpoint: '/api/v1/work-queue/',
  },
  claims_processing: {
    name: 'Claims Processing',
    port: 8153,
    listMode: 'work_queue',
    endpoint: '/api/v1/work-queue/',
  },
  charge_capture: {
    name: 'Charge Capture',
    port: 8152,
    listMode: 'work_queue',
    endpoint: '/api/v1/work-queue/',
  },
  remittance_advice: {
    name: 'Remittance Advice',
    port: 8157,
    listMode: 'work_queue',
    endpoint: '/api/v1/work-queue/',
  },
  patient_billing: {
    name: 'Patient Billing',
    port: 8110,
    listMode: 'work_queue',
    endpoint: '/api/v1/work-queue/',
  },
  contract_management: {
    name: 'Contract Management',
    port: 8156,
    listMode: 'work_queue',
    endpoint: '/api/v1/work-queue/',
  },
  rcm_compliance: {
    name: 'RCM Compliance',
    port: 8158,
    listMode: 'work_queue',
    endpoint: '/api/v1/work-queue/',
  },
};

/**
 * Optional per-service API base (build-time). Same idea as AgenticRCM `getServiceApiUrl` / Charge Capture.
 * When set to a full http(s) URL, work-queue requests use it instead of hostname:port.
 * Set to skip|disabled|off|none to omit this service from aggregated queue (no HTTP call).
 */
const EXPLICIT_API_URL_BY_SERVICE = {
  insurance_verification: process.env.REACT_APP_INSURANCE_VERIFICATION_API_URL,
  prior_authorization: process.env.REACT_APP_PRIOR_AUTHORIZATION_API_URL,
  denial_management: process.env.REACT_APP_DENIAL_MANAGEMENT_API_URL,
  ar_posting: process.env.REACT_APP_AR_POSTING_API_URL,
  claims_processing: process.env.REACT_APP_CLAIMS_PROCESSING_API_URL,
  charge_capture: process.env.REACT_APP_CHARGE_CAPTURE_API_URL,
  remittance_advice: process.env.REACT_APP_REMITTANCE_ADVICE_API_URL,
  patient_billing: process.env.REACT_APP_PATIENT_BILLING_API_URL,
  contract_management: process.env.REACT_APP_CONTRACT_MANAGEMENT_API_URL,
  rcm_compliance: process.env.REACT_APP_RCM_COMPLIANCE_API_URL,
};

function normalizeQueueExplicitUrl(raw) {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  if (['skip', 'disabled', 'off', 'none'].includes(s.toLowerCase())) return null;
  if (/^https?:\/\//i.test(s)) return s.replace(/\/$/, '');
  return null;
}

/**
 * Base URL for one RCM backend used by the shell work queue.
 * - localhost (non-shell): `protocol//host:port`
 * - AgenticRCM (:3160) / Agency (:3161) / remote: nginx `/rcm-api/*` proxies when available
 * - otherwise null (skip call — avoids browser hangs on closed security-group ports)
 */
function shouldUseShellApiProxy() {
  if (process.env.REACT_APP_USE_DEFAULT_RCM_PORTS === 'true') return false;
  if (typeof window === 'undefined' || !window.location) return false;
  const { hostname, port } = window.location;
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
  if (isLocal && (port === '3000' || port === '3001')) return false;
  if (isLocal && port !== '3160' && port !== '3161' && port !== '80' && port !== '443' && port !== '') return false;
  return true;
}

/** Same paths as AgenticRCM shell nginx `/rcm-api/*`. */
export const SHELL_PROXY_BASE_BY_SERVICE = {
  denial_management: '/rcm-api/denial',
  ar_posting: '/rcm-api/ar-posting',
  charge_capture: '/rcm-api/charge-capture',
  claims_processing: '/rcm-api/claims-processing',
  insurance_verification: '/rcm-api/insurance-verification',
  prior_authorization: '/rcm-api/prior-authorization',
  remittance_advice: '/rcm-api/remittance',
  patient_billing: '/rcm-api/patient-billing',
  contract_management: '/rcm-api/contract-management',
  rcm_compliance: '/rcm-api/compliance',
};

/** Resolve API base for one RCM service (shell proxy or host:port). */
export const getServiceBaseUrl = (port, serviceKey) => {
  const explicit = normalizeQueueExplicitUrl(EXPLICIT_API_URL_BY_SERVICE[serviceKey]);
  if (explicit) return explicit;

  if (typeof window === 'undefined' || !window.location) {
    return `http://localhost:${port}`;
  }

  if (shouldUseShellApiProxy()) {
    return SHELL_PROXY_BASE_BY_SERVICE[serviceKey] || null;
  }

  const host = window.location.hostname;
  const protocol = window.location.protocol;
  const isLocal = host === 'localhost' || host === '127.0.0.1';
  const useDefaultPorts = process.env.REACT_APP_USE_DEFAULT_RCM_PORTS === 'true';

  if (!isLocal && !useDefaultPorts) {
    return null;
  }
  return `${protocol}//${host}:${port}`;
};

function createRcmServiceClient(serviceKey, organizationIdOverride) {
  const service = RCM_QUEUE_SERVICES[serviceKey];
  if (!service) return null;
  const baseURL = getServiceBaseUrl(service.port, serviceKey);
  if (!baseURL) return null;
  const client = axios.create({
    baseURL,
    headers: { 'Content-Type': 'application/json' },
  });

  client.interceptors.request.use((config) => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const org =
      organizationIdOverride ||
      localStorage.getItem('selectedOrganizationId') ||
      localStorage.getItem('organizationId') ||
      localStorage.getItem('organization_id');
    config.params = config.params || {};
    if (org && !config.params.organization_id) {
      config.params.organization_id = org;
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response.data,
    (error) => Promise.reject(error)
  );

  return client;
}

function mapDenialToQueueItem(raw, idx, serviceKey, serviceName) {
  const domainId = raw.id || raw.claim_id || `denial-${idx}`;
  const display_queue_id = buildDisplayQueueId(serviceKey, domainId);
  const statusRaw = (raw.queue_status || raw.status || 'pending').toLowerCase().replace(/\s+/g, '_');
  const status = ['pending', 'in_progress', 'completed', 'failed', 'assigned'].includes(statusRaw)
    ? statusRaw
    : 'pending';
  return {
    id: `denial_mgmt-${String(domainId)}`,
    external_id: display_queue_id,
    display_queue_id,
    title: raw.title || raw.patient_name
      ? `Denial — ${raw.patient_name || raw.member_name || 'Claim'}`
      : `Denial — ${String(domainId).slice(0, 12)}`,
    description: raw.denial_reason || raw.reason || raw.notes || '',
    status,
    priority: raw.priority || 'medium',
    item_type: 'denial',
    target_service: serviceKey,
    source_service: serviceKey,
    source_service_name: serviceName,
    assigned_to: raw.assigned_to || raw.assignedTo,
    assigned_to_name: raw.assigned_to_name || raw.assignedTo || raw.assignedToName,
    assigned_to_type: raw.assigned_to_type,
    created_at: raw.denial_date || raw.created_at || new Date().toISOString(),
    updated_at: raw.updated_at || raw.denial_date,
    metadata: {
      ...raw,
      domain_id: String(domainId),
      display_queue_id,
    },
  };
}

function mapEobExceptionToQueueItem(raw, idx, serviceKey, serviceName) {
  const eobId = raw.eob_id || raw.id || `eob-${idx}`;
  const display_queue_id = buildDisplayQueueId(serviceKey, eobId);
  const statusRaw = (raw.queue_status || raw.status || 'pending').toLowerCase().replace(/\s+/g, '_');
  const status = ['pending', 'in_progress', 'completed', 'failed', 'assigned', 'cancelled'].includes(statusRaw)
    ? statusRaw
    : 'pending';
  return {
    id: `remittance_exc-${eobId}`,
    external_id: display_queue_id,
    display_queue_id,
    title: `EOB exception — ${raw.exception_type || 'Review'}`,
    description: raw.eob_summary ? JSON.stringify(raw.eob_summary).slice(0, 200) : '',
    status,
    priority: (raw.priority || 'MEDIUM').toLowerCase(),
    item_type: 'remittance',
    target_service: serviceKey,
    source_service: serviceKey,
    source_service_name: serviceName,
    assigned_to: raw.assigned_to,
    assigned_to_name: raw.assigned_to_name || raw.assigned_to,
    assigned_to_type: raw.assigned_to_type,
    created_at: raw.created_at || new Date().toISOString(),
    metadata: {
      ...raw,
      domain_id: String(eobId),
      display_queue_id,
    },
  };
}

/**
 * @param {string} serviceKey
 * @param {object} filters
 * @param {string} [organizationIdOverride]
 */
export async function getQueueItemsFromRcmService(serviceKey, filters = {}, organizationIdOverride) {
  const service = RCM_QUEUE_SERVICES[serviceKey];
  if (!service) return [];

  const client = createRcmServiceClient(serviceKey, organizationIdOverride);
  if (!client) return [];

  try {
    if (service.listMode === 'denials') {
      const params = {
        skip: filters.skip ?? 0,
        limit: filters.limit ?? 100,
        ...filters,
      };
      const data = await client.get(service.listPath, { params });
      const rows = Array.isArray(data) ? data : data?.items || [];
      return rows.map((row, idx) =>
        mapDenialToQueueItem(row, idx, serviceKey, service.name)
      );
    }

    if (service.listMode === 'eob_exceptions') {
      const params = { ...filters };
      const data = await client.get(service.listPath, { params });
      const rows = Array.isArray(data) ? data : data?.items || [];
      return rows.map((row, idx) =>
        mapEobExceptionToQueueItem(row, idx, serviceKey, service.name)
      );
    }

    const params = {
      limit: filters.limit ?? 100,
      skip: filters.skip ?? 0,
      ...filters,
    };
    const response = await client.get(service.endpoint, { params });
    let items = response;
    if (Array.isArray(response)) items = response;
    else if (response?.items) items = response.items;
    else if (response?.data) items = response.data;
    else items = [];

    return items.map((item) => {
      const domainId =
        item.charge_id ||
        item.eligibility_request_id ||
        item.request_id ||
        item.claim_id ||
        item.posting_id ||
        item.eob_id ||
        item.id ||
        item.external_id;
      const display_queue_id =
        item.display_queue_id ||
        (domainId != null ? buildDisplayQueueId(serviceKey, domainId) : null);
      const meta = item.metadata && typeof item.metadata === 'object' ? item.metadata : {};
      const statusRaw = (item.status || 'pending').toLowerCase().replace(/\s+/g, '_');
      const status = ['pending', 'in_progress', 'completed', 'failed', 'assigned', 'cancelled'].includes(statusRaw)
        ? statusRaw
        : 'pending';
      return {
        ...item,
        status,
        source_service: serviceKey,
        source_service_name: service.name,
        display_queue_id,
        external_id: item.external_id || display_queue_id,
        assigned_to: item.assigned_to,
        assigned_to_name: item.assigned_to_name || item.assigned_to,
        metadata: {
          ...meta,
          domain_id: meta.domain_id != null ? meta.domain_id : String(domainId ?? ''),
          display_queue_id,
        },
      };
    });
  } catch (error) {
    const msg = error?.message || '';
    const isNetwork =
      !error?.response ||
      error.code === 'ERR_NETWORK' ||
      error.code === 'ERR_CONNECTION_REFUSED' ||
      error.code === 'ECONNREFUSED' ||
      error.code === 'ECONNABORTED' ||
      msg.includes('Network Error') ||
      msg.includes('NetworkError') ||
      msg.includes('Failed to fetch') ||
      msg.includes('Connection refused') ||
      msg.includes('timeout');
    if (!isNetwork) {
      console.warn(`[rcmQueueAggregation:${serviceKey}]`, msg);
    }
    return [];
  }
}

/**
 * @param {object} filters
 * @param {string} [organizationIdOverride] — for multi-org shells (Agency)
 * @param {boolean} [allowMockFallback] — AgenticRCM mock when all unreachable
 */
export async function getQueueItemsFromAllRcmServices(
  filters = {},
  organizationIdOverride
) {
  const keys = Object.keys(RCM_QUEUE_SERVICES);
  const results = await Promise.allSettled(
    keys.map((k) => getQueueItemsFromRcmService(k, filters, organizationIdOverride))
  );
  return results
    .filter((r) => r.status === 'fulfilled')
    .map((r) => r.value)
    .flat();
}

export function getRcmQueueServiceList() {
  return Object.keys(RCM_QUEUE_SERVICES).map((key) => ({
    key,
    name: RCM_QUEUE_SERVICES[key].name,
    port: RCM_QUEUE_SERVICES[key].port,
  }));
}
